import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { GlgLoadingSpinner } from 'SharedComponents'
import { GlgCheckbox, GlgTooltipIcon, GlgTextInput } from 'src/shared/components/'
import { callAPI } from 'Shared/helpers'
import { getAuthenticityToken } from 'Shared/helpers'

const StyledPage = styled.div`
  .spinner {
    padding-top: 10px;
  }

  .settings_container {
    display: flex;
    padding: 20px;

    .description {
      width: 25%;
      padding-right: 80px;

      .sub_title {
        font-weight: bold;
        padding-bottom: 10px;
      }
    }
    .list_of_checkboxes {
      display: flex;
      flex-direction: column;

      .check {
        display: flex;

        .icon_div {
          display: flex;
          align-items: center;
          padding-bottom: 4px;
        }
      }

      .set_selected {
        label {
          font-weight: bold;
        }
      }

      .not_clickable {
        pointer-events: none;
      }
    }

    .google_tag_id_container {
      display: flex;
      align-items: center;

      .google_tag_id_input {
        width: 230px;
      }
    }
  }

  .settings_container:not(:first-child) {
    border-top: 1px dashed #DBDBDB;
  }
`

const GolfHubCustomerSettings = (props) => {
  const [ dataIsLoading, setDataIsLoading ] = useState(true)
  const [ settings, setSettings ] = useState({})
  const [ savingSettings, setSavingSettings ] = useState(false)

  useEffect(() => {
    callAPI(`/customers/${props.customerId}/golf_hub_customer_settings`, 'GET')
    .then((json) => {
      setSettings(json)
      setDataIsLoading(false)
    })
  }, [])

  const saveFormData = () => {
    const fd = new FormData()
    
    fd.append('my_hubs', settings['golfhub_page_visibility_my_hubs'])
    fd.append('agg_hubs', settings['golfhub_page_visibility_aggregated_hubs'])
    fd.append('customer_parents', JSON.stringify(settings['customer_parents'] || []))
    fd.append('google_tag_id', settings['google_tag_id'] || '')

    setSavingSettings(true)
    fetch(`/customers/${props.customerId}/golf_hub_customer_settings/${settings['settings_id']}`, {
      method: 'PATCH',
      body: fd,
      headers: {
        'X-CSRF-Token': getAuthenticityToken(),
      },
    })
    .then((response) => {
      if (response.ok) {
        setSavingSettings(false)
        window.show_flash(window.I18n.t('views.golfhub.errors.settings.good_save'))
        window.hide_flash()
      } else {
        setSavingSettings(false)
        window.show_flash(window.I18n.t('views.golfhub.errors.settings.bad_save'))
        window.hide_flash()
      }
    })
  }

  const updateParentIncludeSetting = (parentId) => {
    const updatedSetting = { ...settings }

    updatedSetting.customer_parents = updatedSetting.customer_parents.map((parent) => {
      if (parent.id === parentId) {
        return {
          ...parent,
          is_included_in_parent_hub: !parent.is_included_in_parent_hub,
        }
      }

      return parent
    })

    setSettings(updatedSetting)
  }

  return (
    <StyledPage>
      {dataIsLoading && (
        <GlgLoadingSpinner className='spinner text-center' show={dataIsLoading} />
      )}
      {!dataIsLoading && (
        <>
          <div className="settings_container">
            <div className="description">
              <div className="sub_title">
                { window.I18n.t('views.golfhub.checkbox_setting') }
              </div>
              <div className="long_desc">
                { window.I18n.t('views.golfhub.checkbox_desc_setting') }
              </div>
            </div>
            <div className="list_of_checkboxes">
              <div className={`check my_hubs ${settings['golfhub_page_visibility_my_hubs'] ? 'set_selected' : ''}`}
                  onClick={() => setSettings({...settings, golfhub_page_visibility_my_hubs: !settings['golfhub_page_visibility_my_hubs']})}>
                <div className="not_clickable">
                  <GlgCheckbox
                    name="golfhub_page_visibility_my_hubs"
                    label={ window.I18n.t('views.golfhub.my_hubs') }
                    checked={ settings['golfhub_page_visibility_my_hubs'] }
                  />
                </div>
                <div className="icon_div">
                  <GlgTooltipIcon
                    tooltip = { window.I18n.t('views.golfhub.my_hubs_tooltip') }
                  />
                </div>
              </div>
              {settings['customer_parents'] !== undefined && settings['customer_parents'].length > 0 && settings['customer_parents'].map((setting) => {
                return (
                  <div className={`check parent_checkbox ${setting['is_included_in_parent_hub'] ? 'set_selected' : ''} ${setting['is_parent_active'] ? '' : 'hidden'}`} 
                      key={setting['id']}
                      onClick={() => updateParentIncludeSetting(setting['id'])}>
                    <div className="not_clickable">
                      <GlgCheckbox
                        name={ setting['name'] }
                        label={ setting['name'] + ' ' + window.I18n.t('views.golfhub.hubs') }
                        checked={ setting['is_included_in_parent_hub'] }
                      />
                    </div>
                    <div className="icon_div">
                      <GlgTooltipIcon
                        tooltip = { window.I18n.t('views.golfhub.parent_hubs_tooltip', {parent: setting['name']}) }
                      />
                    </div>
                  </div>
                )
              })}
              <div className={`check agg_hubs ${settings['golfhub_page_visibility_aggregated_hubs'] ? 'set_selected' : ''}`} 
                  onClick={() => setSettings({...settings, golfhub_page_visibility_aggregated_hubs: !settings['golfhub_page_visibility_aggregated_hubs']})}>
                <div className="not_clickable">
                  <GlgCheckbox
                    name="golfhub_page_visibility_aggregated_hubs"
                    label={ window.I18n.t('product') + ' ' + window.I18n.t('views.golfhub.hubs') }
                    checked={ settings['golfhub_page_visibility_aggregated_hubs'] }
                  />
                </div>
                <div className="icon_div">
                  <GlgTooltipIcon
                    tooltip = { window.I18n.t('views.golfhub.golf_genius_hubs_tooltip') }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='settings_container'>
            <div className="description">
              <div className="sub_title">
              { window.I18n.t('views.golfhub.google_tag_id') }
              </div>
            </div>
            <div className='google_tag_id_container'>
              <div className='google_tag_id_input'>
                <GlgTextInput
                  placeholder={'GTM-XXXXXX / G-XXXXXXXXX'}
                  value={settings['google_tag_id'] || ''}
                  onChange={(googleTagId) => setSettings({...settings, google_tag_id: googleTagId})}
                />
              </div>
            </div>
          </div>

          <div className='save_bar'>
            <a className='button btsp_gray_button' onClick={() => {
              window.location.pathname = `/customers/${props.customerId}/hubs`
            }}>{window.I18n.t('views.golfhub.cancel')}</a>
            <a className='button btsp_orange_button' disabled={savingSettings} onClick={() => {
              saveFormData()
            }}>{savingSettings ? window.I18n.t('views.golfhub.saving') :  window.I18n.t('views.golfhub.modal.save')}</a>
          </div>
        </>
      )}
    </StyledPage>
  )
}

GolfHubCustomerSettings.propTypes = {
  customerId: PropTypes.string,
}

export default GolfHubCustomerSettings
